import { Company } from 'domain/company/Company';
import { SuggestedCompanies } from './SuggestedCompanies';
import { VStack, Text } from '@montel/montelpro-shared-components-v2';
import SectionDivider from 'components/common/SectionDivider';
import { CreateCompanySection } from './CreateCompanySection';
import { initialFormState } from 'components/companies/initialFormState';
import { useAssignToCompanyFormContext } from 'components/companies/AssignToCompanyForm/AssignToCompanyFormContext';
import { useState } from 'react';
import { MontelAccount } from 'domain/account/interfaces';
import { CompanySelect } from 'components/companies/CompanySelect';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { ACCOUNT_API_COMPANIES } from 'apiRoutes';
import { RequestLoader } from 'components/common/Loader';

export const AssignToCompanyForm: React.FC<{
	account: MontelAccount;
}> = ({ account }) => {
	const [showCreateCompanySection, setShowCreateCompanySection] =
		useState<boolean>(false);

	const { suggestions, selectedCompany, setSelectedCompany } =
		useAssignToCompanyFormContext();

	const {
		data: companyOptions,
		status: getCompaniesStatus,
		getData: refreshCompanyOptions,
	} = useGetOnMount<Company[]>(`${ACCOUNT_API_COMPANIES}/GetCompanies`);

	const onCompanySelectedFromSuggested = (company: Company) => {
		setSelectedCompany(company);
		setShowCreateCompanySection(false);
	};

	const onCompanyCreated = (company: Company) => {
		setSelectedCompany(company);
		setShowCreateCompanySection(false);
		refreshCompanyOptions();
	};

	const onCompanySelectedFromList = (companyId: number) => {
		const company = companyOptions.find((c) => c.id === companyId);

		setSelectedCompany(company);
		setShowCreateCompanySection(false);
	};

	const onCreateCompanyClicked = () => {
		setSelectedCompany(undefined);
		setShowCreateCompanySection(true);
	};

	const willBeAssignedText = () => {
		if (
			account.company.id &&
			selectedCompany &&
			selectedCompany.id === account.company.id
		)
			return `User is already assigned to ${selectedCompany.name}`;
		else if (selectedCompany)
			return `User will be assigned to ${selectedCompany.name}`;
		else return '';
	};

	return (
		<RequestLoader status={getCompaniesStatus}>
			<SuggestedCompanies
				selectCompany={onCompanySelectedFromSuggested}
			/>
			<VStack gap="md" fullWidth align="center">
				<Text>
					{suggestions.companies.length > 0
						? 'If none of the above are correct, select one from the list'
						: 'Select a company'}
				</Text>
				<CompanySelect
					companies={companyOptions}
					value={selectedCompany ? selectedCompany.id : null}
					onChange={onCompanySelectedFromList}
					label=""
				/>
				<SectionDivider size="sm" fullWidth />
				<CreateCompanySection
					showCreateCompanySection={showCreateCompanySection}
					setShowCreateCompanySection={setShowCreateCompanySection}
					onCreateCompanyClicked={onCreateCompanyClicked}
					initialFormState={initialFormState}
					onCompanyCreated={onCompanyCreated}
				/>
				<SectionDivider size="sm" fullWidth />
				{selectedCompany && (
					<Text size="sm" weight="bold">
						{willBeAssignedText()}
					</Text>
				)}
			</VStack>
		</RequestLoader>
	);
};
